import { Badge } from "@/components/ui/badge";
import { ArrowRightCircle, Flag, ShieldAlert, UserX } from "lucide-react";
import { cn } from "@/lib/utils";
import { flagTone, STAGE_V2_LABEL, stageV2Tone } from "@/pages/inbox/CasePicker";

/** Flag chips from the v2 triage — list rows show the first `max`, the reading pane shows them all. */
export function FlagChips({ flags, idKey, max = 3, size = "text-[9px]" }) {
  const list = flags || [];
  if (!list.length) return null;
  const shown = list.slice(0, max);
  return (
    <>
      {shown.map((f, i) => (
        <Badge key={f} className={cn(size, "gap-0.5 hover:opacity-100 border", flagTone(f))} title={f} data-testid={`flag-badge-${idKey}-${i}`}>
          <Flag className="w-2.5 h-2.5" />{f}
        </Badge>
      ))}
      {list.length > shown.length && (
        <Badge variant="outline" className={cn(size, "text-slate-600")} title={list.slice(max).join(", ")} data-testid={`flag-badge-${idKey}-more`}>+{list.length - shown.length}</Badge>
      )}
    </>
  );
}

export function StageChip({ stage, testid = "reading-pane-stage" }) {
  if (!stage || stage === "n/a") return null;
  return (
    <Badge variant="outline" className={cn("text-[9px]", stageV2Tone(stage))} title="Account stage derived from the file (legal → PDN / DN events → arrears day bands)" data-testid={testid}>
      {STAGE_V2_LABEL[stage] || stage}
    </Badge>
  );
}

/** "Route to: Collections Team Manager — settlement offer £45,000 …" — hints only, nothing is enforced. */
export function RoutingHints({ routing, holdingOnly }) {
  const rows = routing || [];
  if (!rows.length && !holdingOnly) return null;
  return (
    <div className="mt-1.5 space-y-1" data-testid="reading-pane-routing">
      {holdingOnly && (
        <div className="flex items-start gap-1.5 text-[11px] text-rose-900 bg-rose-50 border border-rose-200 rounded px-2 py-1" data-testid="reading-pane-holding-only">
          <ShieldAlert className="w-3 h-3 mt-0.5 shrink-0" />
          <span><b>Holding reply only</b> — the policy says no arrangement, plan or settlement is offered at this stage; the AI draft will acknowledge and hand over.</span>
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-1.5 text-[11px] text-slate-800 bg-slate-50 border border-slate-200 rounded px-2 py-1" data-testid={`reading-pane-route-${i}`}>
          <ArrowRightCircle className="w-3 h-3 mt-0.5 shrink-0 text-indigo-700" />
          <span><b>Route to {r.to}</b> — {r.why}</span>
        </div>
      ))}
    </div>
  );
}

export function UnauthorisedHint({ triage }) {
  if (!triage || triage.authorised !== false) return null;
  return (
    <span className="inline-flex items-center gap-1 text-[10px] text-amber-900 bg-amber-50 border border-amber-300 rounded px-1.5 py-0.5" title="The sender isn't the customer and isn't a contact on the file — check before discussing the account" data-testid="reading-pane-unauthorised">
      <UserX className="w-3 h-3" />{triage.sender_type ? triage.sender_type.replace(/_/g, " ") : "third party"} · not on the file — verify before discussing
    </span>
  );
}
